/* screens/sales/PrivatePlansScreen.jsx — вкладка «Закрытые» раздела «Тарифы» (/console/plans).
   Список ТОЛЬКО закрытых тарифов (рабочий материал менеджера). Клик по строке → дровер
   правки, PATCH шлём частичным. Страница, заголовок и вкладки — PlansScreen; доступ к
   вкладке — private_plans_access (subscriptions.manage_private_plans), см. plansAccess.js.
   Публичной линейки здесь нет: у неё своя вкладка «Публичные». Прежний экран держал её
   ниже закрытых справочной секцией, и после появления вкладки владелец видел под
   вкладкой «Закрытые» два раздела — «Закрытые 6» и «Публичные 31». Эндпоинт консоли
   отдаёт все тарифы, поэтому отбор закрытых — здесь (privatePlansOf). */
import React from 'react';
import { EmptyState, ErrorState } from '../../components/patterns/States.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { Field, TextInput, Textarea, NumberInput, SearchInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Switch } from '../../components/ui/Switch.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';
import * as api from '../../lib/api/salesConsole.js';
import {
  KNOWN_FEATURES, SLUG_RE, INT_FIELDS, LIMIT_FIELDS, num, limitText, periodText, seatsText, featureObj, sortedJson,
  featureRowsOf, validateName, validatePrice, validateInts, validateNote, FeaturesField,
} from './planForm.jsx';
import { PLAN_TABS, periodCustomOf, withCustomDays, withPeriodChoice } from './planEditor.js';
import { AudienceField, EditorTabs, PeriodField, TabPanel, Toggle, useEditorTabs } from './planEditorParts.jsx';

const { useState, useMemo, useCallback } = React;
const { useSearchParams } = RR;
const { useFakeQuery } = hooks;

// Запрет суффикса периода — правило только закрытых: `_6m`/`_12m` зарезервированы
// за вариантами срока публичной линейки (их заводит вкладка «Публичные»).
const PERIOD_SUFFIX_RE = /_\d+m$/;

// Подстраховка на случай ответа без detail — сам detail с бэка уже по-русски и точнее.
const ERROR_FALLBACK = {
  slug_taken: 'Тариф с таким слагом уже есть.',
  default_plan_locked: 'Тариф по умолчанию так менять нельзя.',
  not_found: 'Тариф не найден — возможно, слаг изменили.',
};

/* ── Строка списка ── */
function PlanRow({ plan, audienceLabel, onOpen }) {
  const inUse = plan.workspaces_count || 0;
  return (
    <button type="button" className="sc-plans-row" onClick={() => onOpen(plan)}>
      <span className="sc-plans-main">
        <span className="sc-plans-main__name">{plan.name}</span>
        <span className="sc-plans-main__slug num">{plan.slug}</span>
        {plan.sales_note && <span className="sc-plans-main__note">{plan.sales_note}</span>}
      </span>
      <span className="sc-plans-cell num" data-k="Цена">{fmt.money(Number(plan.price_rub))}</span>
      <span className="sc-plans-cell" data-k="Период">
        {periodText(plan.period_days)}
        <span className="sc-plans-sub">{plan.renewable ? 'с автопродлением' : 'разовый'}</span>
      </span>
      <span className="sc-plans-cell" data-k="Запросов">
        <span className="num">{limitText(plan.chat_messages_per_30d)}</span>
        <span className="sc-plans-sub">голос {limitText(plan.voice_seconds_per_30d)}</span>
      </span>
      <span className="sc-plans-cell" data-k="Аудитория">{audienceLabel[plan.audience] || plan.audience}</span>
      <span className={'sc-plans-cell' + (inUse ? ' sc-plans-cell--strong' : '')} data-k="На тарифе">
        {inUse ? seatsText(inUse) : '—'}
      </span>
      {/* Пилюли «Закрытый» нет: во вкладке «Закрытые» других строк не бывает, и
          одинаковая метка в каждой строке была бы шумом. */}
      <span className="sc-plans-cell sc-plans-tags" data-k="Статус">
        <Pill tone={plan.is_active ? 'ok' : 'neutral'} size="sm">{plan.is_active ? 'Активен' : 'Выключен'}</Pill>
        {plan.is_default && <Pill tone="accent" glyph={false} size="sm">По умолчанию</Pill>}
      </span>
    </button>
  );
}

/* ── Список закрытых ──
   Без своего заголовка и счётчика: вкладка уже называется «Закрытые», и второй
   «Закрытые 6» под ней только повторял бы её. Остаётся одна строка о том, что такое
   закрытый тариф. */
function PlanList({ plans, audienceLabel, onOpen, emptyEl }) {
  return (
    <section className="sc-plans-section">
      <p className="sc-plans-section__hint t-caption">
        Не показываются в витрине: клиент покупает их только по именной ссылке на оплату.
      </p>
      {plans.length === 0 ? emptyEl : (
        <div className="sc-plans-table-wrap">
          <div className="sc-plans-table">
            <div className="sc-plans-row sc-plans-row--head">
              <span>Тариф</span><span>Цена</span><span>Период</span><span>Запросов</span>
              <span>Аудитория</span><span>На тарифе</span><span>Статус</span>
            </div>
            {plans.map((p) => (
              <PlanRow key={p.slug} plan={p} audienceLabel={audienceLabel} onOpen={onOpen} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/* ── Форма создания/правки ──
   Один компонент на оба режима: у создания и правки одинаковый набор полей,
   расходятся только слаг (после создания бэк его не принимает) и способ отправки. */
function toForm(plan) {
  const p = plan || {};
  const rows = plan
    // У существующего тарифа показываем ровно те ключи, что в нём есть: добавлять
    // «известные» с false значило бы дописывать в features то, чего там не было.
    // Только флаги — см. featureRowsOf.
    ? featureRowsOf(p.features)
    // Новый заводится под сетевого бизнес-клиента (дефолт эндпоинта) — он получает
    // тот же продукт, что и публичный аналог, поэтому обе фичи включены сразу.
    : KNOWN_FEATURES.map((key) => ({ key, on: true }));
  const periodDays = p.period_days ?? 30;
  return {
    slug: p.slug || '',
    name: p.name || '',
    price_rub: p.price_rub != null ? String(Number(p.price_rub)) : '',
    period_days: periodDays,
    // Выбран срок «Свой» (дни вне 1/2/3/6/12 мес.) и его дни — только для вёрстки, в
    // запрос не уходят: buildSaveBody берёт ключи списком. Свои дни тарифа (365, 45)
    // помнятся, чтобы «Свой» после «12 мес.» вернул их, а не оставил 360 — иначе
    // «Сохранить» молча укоротил бы тариф всем, кто на нём.
    period_custom: periodCustomOf(periodDays),
    period_custom_days: periodCustomOf(periodDays) ? periodDays : '',
    renewable: p.renewable ?? true,
    chat_messages_per_30d: p.chat_messages_per_30d ?? 300,
    voice_seconds_per_30d: p.voice_seconds_per_30d ?? 4320,
    max_documents: p.max_documents ?? 50,
    max_storage_mb: p.max_storage_mb ?? 1024,
    max_file_size_mb: p.max_file_size_mb ?? 25,
    audience: p.audience || 'business',
    sales_note: p.sales_note || '',
    is_private: p.is_private ?? true,
    is_active: p.is_active ?? true,
    features: rows,
  };
}

export function validate(form, isNew) {
  const e = {};
  const nameErr = validateName(form.name);
  if (nameErr) e.name = nameErr;

  if (isNew) {
    const slug = form.slug.trim().toLowerCase();
    if (!SLUG_RE.test(slug)) e.slug = 'Латиница в нижнем регистре, цифры, «-» и «_», 2–64 символа.';
    else if (PERIOD_SUFFIX_RE.test(slug)) e.slug = 'Суффикс «_6m»/«_12m» зарезервирован за вариантами периода публичной линейки.';
  }

  const priceErr = validatePrice(form.price_rub);
  if (priceErr) e.price_rub = priceErr;

  Object.assign(e, validateInts(form, INT_FIELDS));

  const noteErr = validateNote(form.sales_note);
  if (noteErr) e.sales_note = noteErr;
  return e;
}

/* Что реально изменилось: PATCH обязан быть частичным — иначе правка одной
   цены переписала бы и те поля, которых менеджер не касался. Вынесено из дровера
   без изменений, чтобы правило было проверяемо тестом (PrivatePlansScreen.test.js). */
export function changedKeys(initial, form) {
  const out = [];
  if (form.name.trim() !== initial.name.trim()) out.push('name');
  if (num(form.price_rub) !== num(initial.price_rub)) out.push('price_rub');
  INT_FIELDS.forEach(({ key }) => { if (num(form[key]) !== num(initial[key])) out.push(key); });
  // `is_private` в список не входит: сервер его из тела не принимает, и
  // «изменение», которое он не применит, не должно считаться изменением —
  // иначе кнопка «Сохранить» оживает, а тост врёт об успехе.
  ['renewable', 'is_active'].forEach((k) => { if (!!form[k] !== !!initial[k]) out.push(k); });
  if (form.audience !== initial.audience) out.push('audience');
  if (form.sales_note.trim() !== initial.sales_note.trim()) out.push('sales_note');
  if (sortedJson(featureObj(form.features)) !== sortedJson(featureObj(initial.features))) out.push('features');
  return out;
}

function fieldValue(form, key) {
  if (key === 'name') return form.name.trim();
  if (key === 'sales_note') return form.sales_note.trim();
  if (key === 'price_rub') return String(form.price_rub).trim();
  // Только флаги формы: нефлаговые ключи (`expiry_notice_days`) в тело не попадают
  // вовсе, и сервер сохраняет их сам — `!!14` не превратит их в true.
  if (key === 'features') return featureObj(form.features);
  if (INT_FIELDS.some((f) => f.key === key)) return Number(form[key]);
  if (key === 'audience') return form.audience;
  return !!form[key];
}

// Тело запроса: создание — полный набор полей + слаг, правка — только изменённые;
// правка без изменений — null (запроса нет, дровер просто закрывается).
// `is_private` не отправляем: закрытость ставит сам сервер (консоль заводит
// только закрытые тарифы) и из тела запроса её не читает.
export function buildSaveBody(initial, form, isNew) {
  const keys = isNew
    ? ['name', 'price_rub', ...INT_FIELDS.map((f) => f.key), 'renewable', 'audience', 'features', 'is_active', 'sales_note']
    : changedKeys(initial, form);
  if (!isNew && keys.length === 0) return null;
  const body = {};
  keys.forEach((k) => { body[k] = fieldValue(form, k); });
  if (isNew) body.slug = form.slug.trim().toLowerCase();
  return body;
}

// detail с бэка написан по-русски и точен — показываем его как есть.
export function saveErrorDetail(res) {
  return (res.data && res.data.detail) || ERROR_FALLBACK[res.data && res.data.code] || 'Не удалось сохранить тариф.';
}

// Коды, у которых виноватое поле известно, — на поле (и его вкладку): иначе текст
// висел бы над вкладками, а «Активен» на вкладке «Продажи» оставался бы чистым.
// Слаг — только у создания: у правки поля слага нет. Прочие коды — только текстом
// над формой: сервер не говорит, какое из полей виновато.
export function saveErrorFields(res, isNew) {
  const code = res && res.data && res.data.code;
  const detail = saveErrorDetail(res || {});
  if (code === 'slug_taken' && isNew) return { slug: detail };
  if (code === 'default_plan_locked') return { is_active: detail };
  return {};
}

/* Дровер закрытого тарифа — по тем же вкладкам, что у публичного (владелец 18.10):
   «Основное», «Цена и срок», «Лимиты и возможности», «Продажи». Скидки к помесячной
   цене здесь нет: у закрытого тарифа нет семейства с месячной основой. Тела запросов
   прежние — buildSaveBody берёт ключи списком, поля вёрстки (period_custom) в них
   не попадают. */
export function PlanFormDrawer({ plan, onClose, onSaved }) {
  const toast = useToast();
  const isNew = !plan;
  const [initial] = useState(() => toForm(plan));
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const { tab, setTab, ref, showErrors } = useEditorTabs();

  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: '' })); setFormError(''); };
  const setFeatures = (rows) => { setForm((f) => ({ ...f, features: rows })); setErrors((e) => ({ ...e, features: '' })); };
  // Срок выбором «N мес.» пишет те же period_days, что раньше набирали руками (30 × N).
  const setPeriod = (fn) => { setForm(fn); setErrors((e) => ({ ...e, period_days: '' })); setFormError(''); };
  const onPeriod = (c) => setPeriod((f) => withPeriodChoice(f, c, null));
  const onDays = (v) => setPeriod((f) => withCustomDays(f, v));

  const inUse = plan ? (plan.workspaces_count || 0) : 0;
  const isDefault = !!(plan && plan.is_default);

  const changed = useMemo(() => changedKeys(initial, form), [form, initial]);

  const save = async () => {
    const e = validate(form, isNew);
    if (Object.keys(e).some((k) => e[k])) {
      setErrors(e);
      setFormError('Проверьте отмеченные поля.');
      showErrors(e);
      return;
    }
    const body = buildSaveBody(initial, form, isNew);
    if (!body) { onClose(); return; }

    setSaving(true);
    const res = isNew ? await api.createConsolePlan(body) : await api.updateConsolePlan(plan.slug, body);
    setSaving(false);
    if (!res.ok) {
      const detail = saveErrorDetail(res);
      setFormError(detail);
      toast.show(detail, 'danger');
      const fields = saveErrorFields(res, isNew);
      setErrors((x) => ({ ...x, ...fields }));
      showErrors(fields);
      return;
    }
    onSaved(isNew ? 'Тариф создан' : 'Тариф сохранён');
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={saving}>Отмена</Button>
      <div className="grow" />
      <Button variant="primary" loading={saving} onClick={save} disabled={!isNew && changed.length === 0}>
        {isNew ? 'Создать тариф' : 'Сохранить'}
      </Button>
    </>
  );

  const label = (v) => PLAN_TABS.find((t) => t.value === v).label;

  return (
    <Drawer open onClose={onClose} size="lg" footer={footer}
      title={isNew ? 'Новый закрытый тариф' : (form.name || plan.slug)}
      breadcrumbs={isNew ? 'Новый тариф' : `Закрытый тариф · ${plan.slug}`}>
      <div className="sc-plans-form stack stack-12" ref={ref}>
        {inUse > 0 && (
          <div className="sc-plans-warn" role="status">
            <Icon name="alert-triangle" size={16} />
            <span>
              На этом тарифе {seatsText(inUse)}. Условия действующих подписок берутся из тарифа —
              правка затронет и их, а не только новые продажи. Уже выставленные ссылки на оплату
              держат сумму, зафиксированную при выпуске.
            </span>
          </div>
        )}
        {isDefault && (
          <div className="sc-plans-warn sc-plans-warn--info" role="status">
            <Icon name="info" size={16} />
            <span>Это тариф по умолчанию — его выдают каждой новой регистрации. Выключить его
              или сделать закрытым сервер не даст.</span>
          </div>
        )}
        {formError && (
          <div className="sc-plans-error" role="alert"><Icon name="alert-circle" size={14} /><span>{formError}</span></div>
        )}

        <EditorTabs value={tab} onChange={setTab} errors={errors} />

        <TabPanel value="main" label={label('main')} active={tab === 'main'}>
          <Field label="Название" hint="Видит клиент — в витрине, в счёте и на странице оплаты." error={errors.name} required>
            <TextInput value={form.name} invalid={!!errors.name} placeholder="Business Сеть XL"
              onChange={(e) => set('name')(e.target.value)} />
          </Field>
          <AudienceField value={form.audience} onChange={set('audience')} error={errors.audience} />
          {/* Слаг — только при создании: после него он не меняется (на него ссылаются
              платежи и выданные ссылки) и виден в шапке карточки. */}
          {isNew && (
            <Field label="Слаг" error={errors.slug} required
              hint="Идентификатор тарифа: латиница в нижнем регистре, цифры, «-» и «_». После создания не меняется.">
              <TextInput value={form.slug} invalid={!!errors.slug} placeholder="biz_net_xl"
                onChange={(e) => set('slug')(e.target.value)} autoComplete="off" spellCheck={false} />
            </Field>
          )}
          <Field label="Пометка для продаж" hint="Внутренняя заметка: кому и почему выдаём эти условия. Клиент её не видит." error={errors.sales_note}>
            <Textarea value={form.sales_note} minRows={2} invalid={!!errors.sales_note}
              onChange={(e) => set('sales_note')(e.target.value)} />
          </Field>
        </TabPanel>

        <TabPanel value="price" label={label('price')} active={tab === 'price'}>
          <PeriodField form={form} onChoice={onPeriod} onDays={onDays} error={errors.period_days} />
          <Field label="Цена за период" error={errors.price_rub} required hint="Сколько клиент платит за один период.">
            <NumberInput value={form.price_rub} unit="₽" min={0} invalid={!!errors.price_rub} onChange={set('price_rub')}
              aria-label="Цена за период" />
          </Field>
          <div className="sc-plans-toggles">
            <Toggle title="Автопродление" checked={form.renewable} onChange={set('renewable')} error={errors.renewable}
              hint="Подписка продлевается сама, пока клиент не отменит. Выключено — разовый доступ на период." />
          </div>
        </TabPanel>

        <TabPanel value="limits" label={label('limits')} active={tab === 'limits'}>
          <div className="sc-plans-note">
            <Icon name="info" size={14} />
            <span>В лимитах <b className="num">-1</b> — безлимит, <b className="num">0</b> — функция на тарифе запрещена.
              У размера файла безлимита нет: только положительное число.</span>
          </div>
          <div className="sc-plans-grid">
            {LIMIT_FIELDS.map(({ key, label: title, unit, min, hint }) => (
              <Field key={key} label={title} hint={hint} error={errors[key]}>
                <NumberInput value={form[key]} unit={unit || undefined} min={min}
                  invalid={!!errors[key]} onChange={set(key)} aria-label={title} />
              </Field>
            ))}
          </div>
          <FeaturesField rows={form.features} onChange={setFeatures} error={errors.features}
            onError={(m) => setErrors((e) => ({ ...e, features: m }))} />
        </TabPanel>

        <TabPanel value="sales" label={label('sales')} active={tab === 'sales'}>
          <div className="sc-plans-toggles">
            <div className="sc-plans-toggle">
              <span className="sc-plans-toggle__text">
                <span className="sc-plans-toggle__title">Активен</span>
                {/* НЕ обещать здесь, что клиенты «съедут» на дефолтный тариф:
                    reconcile_subscription смотрит только на срок подписки и
                    никогда на plan.is_active, а find_due_for_renewal не
                    фильтрует по нему — рекуррент продолжит списывать. Выключение
                    закрывает ПРОДАЖУ, а не обслуживание. */}
                <span className="sc-plans-toggle__hint">
                  Выключенный тариф нельзя выставить ссылкой — он закрыт для новых продаж.
                  Действующие подписки на нём продолжают работать и продлеваться:
                  чтобы увести клиента, смените ему тариф отдельно.
                </span>
                {errors.is_active && <span className="sc-pub-field-error" role="alert">{errors.is_active}</span>}
              </span>
              <Switch checked={!!form.is_active} onChange={set('is_active')} disabled={isDefault} ariaLabel="Активен" />
            </div>
            <div className="sc-plans-toggle">
              <span className="sc-plans-toggle__text">
                <span className="sc-plans-toggle__title">Закрытый тариф</span>
                {/* Переключатель заблокирован, и это не временная заглушка.
                    Закрытость — граница фичи, а не свойство тарифа: снять её —
                    выложить цену вдвое ниже публичной в витрину всем клиентам
                    (её же читает гейт покупки), поставить на публичный — молча
                    убрать действующий тариф из продажи. Сервер поле из тела не
                    принимает; живой переключатель врал бы — зелёный тост при
                    проигнорированном изменении. */}
                <span className="sc-plans-toggle__hint">
                  Не показывается в общей витрине — купить можно только по именной ссылке на оплату.
                  Консоль заводит только закрытые тарифы; сделать тариф публичным можно лишь релизом.
                </span>
              </span>
              <Switch checked disabled ariaLabel="Закрытый тариф" />
            </div>
          </div>
        </TabPanel>

        {!isNew && (
          <div className="sc-plans-meta t-caption">
            Создан {fmt.date(plan.created_at)} · изменён {fmt.date(plan.updated_at)}
          </div>
        )}
      </div>
    </Drawer>
  );
}

/* Закрытые тарифы из ответа консоли (он несёт и публичные — у вкладки «Публичные»
   свой эндпоинт), с поиском по названию, слагу и пометке. Порядок — по цене: менеджер
   читает закрытые условия как лесенку S → M → L. */
export function privatePlansOf(plans, q) {
  const needle = String(q || '').trim().toLowerCase();
  const match = (p) => !needle || `${p.name} ${p.slug} ${p.sales_note || ''}`.toLowerCase().includes(needle);
  const byPrice = (a, b) => (Number(a.price_rub) - Number(b.price_rub)) || a.slug.localeCompare(b.slug);
  return (Array.isArray(plans) ? plans : []).filter((p) => p && p.is_private && match(p)).sort(byPrice);
}

/* ── Вкладка «Закрытые» ──
   Только тело вкладки: страницу, заголовок «Тарифы» и переключатель вкладок держит
   PlansScreen. */
function PrivatePlansPanel() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');

  // salesConsole не бросает на HTTP-кодах — приводим к контракту useFakeQuery сами.
  const query = useFakeQuery('console-plans', async () => {
    const res = await api.listConsolePlans();
    if (!res.ok) {
      throw Object.assign(new Error((res.data && res.data.detail) || 'Не удалось загрузить тарифы.'),
        { status: res.status });
    }
    return res.data;
  }, []);

  const plans = (query.data && query.data.plans) || [];
  const audiences = (query.data && query.data.audiences) || [];
  const audienceLabel = useMemo(
    () => Object.fromEntries(audiences.map((a) => [a.value, a.label])), [audiences]);

  const privatePlans = useMemo(() => privatePlansOf(plans, q), [plans, q]);

  // Открытый дровер живёт в URL (?plan=new|<слаг>) — ссылку на тариф можно кинуть коллеге.
  const openSlug = params.get('plan') || '';
  const setOpenSlug = useCallback((v) => setParams((prev) => {
    const sp = new URLSearchParams(prev);
    if (v) sp.set('plan', v); else sp.delete('plan');
    return sp;
  }), [setParams]);
  // Только закрытый: ?plan=<публичный слаг> (ссылка из вкладки «Публичные», где у ?plan=
  // свой смысл) ничего здесь не открывает — редактор закрытых его не сохранит.
  const editing = openSlug && openSlug !== 'new'
    ? plans.find((p) => p.slug === openSlug && p.is_private) || null : null;
  const drawerOpen = openSlug === 'new' || !!editing;

  const closeDrawer = useCallback(() => setOpenSlug(''), [setOpenSlug]);

  const openRow = useCallback((p) => setOpenSlug(p.slug), [setOpenSlug]);
  const onSaved = useCallback((message) => {
    closeDrawer();
    query.reload();
    toast.show(message, 'success');
  }, [closeDrawer, query, toast]);

  if (query.loading && !query.data) {
    return <div className="state state--loading"><Spinner size="lg" label="Загрузка тарифов…" /></div>;
  }
  if (query.error) {
    return (
      <ErrorState title="Не удалось загрузить тарифы"
        body={query.error.message || 'Попробуйте ещё раз.'} onRetry={query.reload} />
    );
  }

  const nothingFound = !!q.trim() && privatePlans.length === 0;

  return (
    <>
      <div className="stack stack-16">
        <div className="sc-plans-toolbar">
          <SearchInput value={q} onChange={setQ} placeholder="Поиск по названию, слагу, пометке…"
            className="sc-plans-search" />
          <div className="grow" />
          <Button variant="primary" iconLeft="plus" onClick={() => setOpenSlug('new')}>Новый закрытый тариф</Button>
        </div>

        {nothingFound ? (
          <EmptyState icon="search" title="Ничего не нашлось"
            body="Проверьте запрос — ищем по названию, слагу и пометке для продаж."
            action={<Button variant="secondary" onClick={() => setQ('')}>Сбросить поиск</Button>} />
        ) : (
          <PlanList
            plans={privatePlans} audienceLabel={audienceLabel} onOpen={openRow}
            emptyEl={<EmptyState icon="lock" title="Закрытых тарифов пока нет"
              body="Заведите индивидуальные условия для сетевого клиента — цена, запросы и лимиты задаются здесь."
              action={<Button variant="primary" iconLeft="plus" onClick={() => setOpenSlug('new')}>Новый закрытый тариф</Button>} />}
          />
        )}
      </div>

      {drawerOpen && (
        <PlanFormDrawer key={openSlug} plan={editing} onClose={closeDrawer} onSaved={onSaved} />
      )}
    </>
  );
}

export { PrivatePlansPanel, toForm };
